import React, { useState, useEffect } from 'react';
import ShipmentTracking from '../../components/ShipmentTracking';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, MapPin, CreditCard, Truck, CheckCircle, Clock, XCircle, ArrowLeft, MessageSquare, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ordersApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { formatPrice } from '../../utils/currency';

const OrderDetailPage = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const currentLang = i18n.language || 'en';
  const dateLocale = currentLang.startsWith('es') ? 'es-CO' : 'en-US';
  const statusLabel = (status) => t(`orders.${status}`, { defaultValue: status });
  const paymentLabel = (status) => t(`accountOrders.paymentStatus.${status}`, { defaultValue: status });

  useEffect(() => {
    fetchOrder();
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/account/orders');
        return;
      }

      const data = await ordersApi.getOrderByNumber(orderNumber, token);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast({
        title: t('common.error'),
        description: t('orderConfirm.failedLoadOrder'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm(t('accountOrders.confirmCancel'))) return;

    try {
      setCancelling(true);
      const token = localStorage.getItem('accessToken');
      await ordersApi.cancelOrder(order.id || order._id, token);
      toast({
        title: t('accountOrders.orderCancelled'),
        description: t('accountOrders.orderCancelledDesc'),
      });
      await fetchOrder();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('accountOrders.failedCancel'),
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      processing: Package,
      shipped: Truck,
      delivered: CheckCircle,
      cancelled: XCircle,
    };
    return icons[status] || Clock;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50',
      processing: 'text-blue-600 bg-blue-50',
      shipped: 'text-purple-600 bg-purple-50',
      delivered: 'text-green-600 bg-green-50',
      cancelled: 'text-red-600 bg-red-50',
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <p className="text-gray-600 mb-4">{t('checkout.orderNotFound')}</p>
        <Button onClick={() => navigate('/account/orders')}>{t('accountOrders.backToOrders')}</Button>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(order.status);
  const canCancel = ['pending', 'processing'].includes(order.status);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/account/orders')}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('accountOrders.backToOrders')}
      </Button>

      {/* Order Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-semibold text-[#2C2420] mb-1">
              {t('checkoutSuccess.orderNumberLabel', { n: order.orderNumber })}
            </h2>
            <p className="text-gray-600">
              {t('accountOrders.placedOn', { date: new Date(order.createdAt).toLocaleDateString(dateLocale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }) })}
            </p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${getStatusColor(order.status)}`}>
            <StatusIcon className="h-5 w-5" />
            <span className="font-semibold capitalize">{statusLabel(order.status)}</span>
          </div>
        </div>

        {/* Status Timeline */}
        {order.statusHistory && order.statusHistory.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-semibold mb-4">{t('accountOrders.orderTimeline')}</h3>
            <div className="space-y-3">
              {order.statusHistory.map((history, index) => (
                <div key={index} className="flex gap-3">
                  <div className="w-2 h-2 bg-[#be1f1a] rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium capitalize">{statusLabel(history.status)}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(history.timestamp).toLocaleString(dateLocale)}
                    </p>
                    {history.note && <p className="text-sm text-gray-600 mt-1">{history.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Order Items */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="h-5 w-5" />
          {t('orderConfirm.orderItems')}
        </h3>
        <div className="space-y-4">
          {order.items.map((item, index) => (
            <div key={index} className="flex gap-4 pb-4 border-b last:border-b-0">
              <img
                src={item.image || 'https://placehold.co/80x100'}
                alt={item.name?.[currentLang] || item.name?.en || t('accountOrders.product')}
                className="w-20 h-24 object-cover rounded"
              />
              <div className="flex-1">
                <p className="font-medium">{item.name?.[currentLang] || item.name?.en || t('accountOrders.product')}</p>
                <p className="text-sm text-gray-500">{t('orderConfirm.sku')}: {item.sku}</p>
                <p className="text-sm text-gray-500">{t('checkoutSuccess.quantity')}: {item.quantity}</p>
                {(order.status === 'delivered' || order.status === 'shipped') && item.productSlug && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 text-[#be1f1a] border-[#be1f1a] hover:bg-[#be1f1a] hover:text-white"
                    onClick={() => navigate(`/product/${item.productSlug}?review=true`)}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    {t('accountOrders.writeReview')}
                  </Button>
                )}
              </div>
              <div className="text-right">
                <p className="font-semibold text-[#be1f1a]">
                  {formatPrice(item.total || 0, order.currency || 'COP')}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="mt-6 pt-6 border-t space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('checkout.subtotal')}:</span>
            <span>
              {formatPrice(order.subtotal || 0, order.currency || 'COP')}
            </span>
          </div>
          
          {/* Display discount (new format) */}
          {order.discount_amount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>{t('checkout.discount')}:</span>
              <span>
                -{formatPrice(order.discount_amount, order.currency || 'COP')}
              </span>
            </div>
          )}
          
          {/* Display discount (old format - for backward compatibility) */}
          {order.discount > 0 && !order.discount_amount && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('checkout.discount')}:</span>
              <span className="text-green-600">
                -{formatPrice(order.discount || 0, order.currency || 'COP')}
              </span>
            </div>
          )}
          
          {/* Display detailed tax info if available (new format) */}
          {order.tax_info && order.tax_info.tax_amount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                {order.tax_info.tax_name} ({order.tax_info.tax_rate}%):
                {order.tax_info.included_in_price && (
                  <span className="text-xs ml-1">({t('checkout.taxIncluded').toLowerCase()})</span>
                )}
              </span>
              <span>
                {order.tax_info.included_in_price ? (
                  <span className="text-gray-500">{t('checkout.taxIncluded')}</span>
                ) : (
                  formatPrice(order.tax_info.tax_amount, order.currency || 'COP')
                )}
              </span>
            </div>
          )}
          
          {/* Display simple tax if no detailed info (old format) */}
          {!order.tax_info && order.tax > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('orderConfirm.tax')}:</span>
              <span>
                {formatPrice(order.tax || 0, order.currency || 'COP')}
              </span>
            </div>
          )}
          
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('checkout.shipping')}:</span>
            <span>
              {order.shippingCost === 0 ? t('orderConfirm.free') : 
                formatPrice(order.shippingCost || 0, order.currency || 'COP')
              }
            </span>
          </div>
          <div className="flex justify-between text-lg font-semibold pt-2 border-t">
            <span>{t('orders.total')}:</span>
            <span className="text-[#be1f1a]">
              {formatPrice(order.total || 0, order.currency || 'COP')}
            </span>
          </div>
        </div>
      </div>

      <ShipmentTracking shipment={order.shipment} className="shadow-md" />

      {/* Addresses & Payment */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Shipping Address */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t('checkout.shippingAddress')}
          </h3>
          <div className="text-sm text-gray-600 space-y-1">
            <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
            <p>{order.shippingAddress.addressLine1}</p>
            {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
            <p>
              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
            </p>
            <p>{order.shippingAddress.country}</p>
            <p className="pt-2">{t('checkout.phone')}: {order.shippingAddress.phone}</p>
          </div>
        </div>

        {/* Payment Info */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t('checkoutMore.paymentInformation')}
          </h3>
          <div className="text-sm text-gray-600 space-y-2">
            <p className="font-medium text-gray-900 capitalize">{order.paymentMethod}</p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
              <CheckCircle className="w-4 h-4" />
              {t('orderConfirm.paymentStatus', { status: paymentLabel(order.paymentStatus) })}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">{t('accountOrders.orderActions')}</h3>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => navigate(`/support/create?orderId=${order.id || order._id}&orderNumber=${order.orderNumber}`)}
            variant="outline"
            className="border-[#be1f1a] text-[#be1f1a] hover:bg-[#be1f1a] hover:text-white"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            {t('accountOrders.contactSupport')}
          </Button>
          {canCancel && (
            <Button
              onClick={handleCancelOrder}
              disabled={cancelling}
              variant="destructive"
            >
              {cancelling ? t('accountOrders.cancelling') : t('accountOrders.cancelOrder')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;